import { useEffect, useRef, useState } from 'react';
import { type AudioEvent, audio, type Player } from '../../core/audio';
import { getRecord, setRecord } from '../../core/store';
import { clamp } from '../../core/util';
import { Btn, IconBtn } from '../../ui/Button';
import { ToolShell } from './ToolShell';

interface Opts {
  bpm: number;
  beats: number;
  sub: boolean;
  acc: boolean;
}

export function Metronome() {
  const [o, setO] = useState<Opts>(() => ({
    bpm: getRecord<number>('metro.bpm') || 90,
    beats: 4,
    sub: false,
    acc: true,
  }));
  const [on, setOn] = useState(false);
  const [beat, setBeat] = useState(-1);
  const player = useRef<Player | null>(null);
  const taps = useRef<number[]>([]);

  const restart = (next: Opts) => {
    player.current?.stop();
    const beatSec = 60 / next.bpm;
    const ev: AudioEvent[] = [];
    for (let b = 0; b < next.beats; b++) {
      ev.push({
        kind: 'drum',
        drum: b === 0 && next.acc ? 'clickHi' : 'click',
        t: b * beatSec,
        vel: b === 0 ? 0.9 : 0.65,
        cue: { type: 'beat', k: b },
      });
      if (next.sub) ev.push({ kind: 'drum', drum: 'click', t: b * beatSec + beatSec / 2, vel: 0.3 });
    }
    player.current = audio.play(ev, {
      length: next.beats * beatSec,
      loop: true,
      onCue: (cue) => {
        if (cue.type === 'beat') setBeat(cue.k);
      },
    });
  };

  const update = (patch: Partial<Opts>) => {
    const next = { ...o, ...patch };
    setO(next);
    if (on) restart(next);
  };

  const setBpm = (v: number) => {
    const bpm = clamp(Math.round(v), 40, 220);
    setRecord('metro.bpm', bpm);
    update({ bpm });
  };

  useEffect(() => () => player.current?.stop(), []);

  return (
    <ToolShell
      title="Metronom"
      desc="Vuruşu say, akor değişimlerini zamanında yap. Kulak eğitiminin ritim tarafı."
      icon="metronome"
    >
      <div className="metro">
        <div className="metro-bpm">
          {o.bpm}
          <small>BPM</small>
        </div>
        <div className="metro-dots">
          {Array.from({ length: o.beats }, (_, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: one dot per beat
              key={i}
              className={`metro-dot${i === 0 && o.acc ? ' acc' : ''}${on && i === beat ? ' on' : ''}`}
            />
          ))}
        </div>
        <div className="metro-row">
          <IconBtn icon="minus" label="Yavaşlat" onClick={() => setBpm(o.bpm - 1)} />
          <input
            id="metro-bpm"
            aria-label="Tempo (BPM)"
            type="range"
            min="40"
            max="220"
            step="1"
            value={o.bpm}
            onChange={(e) => setBpm(Number.parseInt(e.target.value, 10))}
          />
          <IconBtn icon="plus" label="Hızlandır" onClick={() => setBpm(o.bpm + 1)} />
        </div>
        <div className="metro-row">
          <Btn
            kind="primary"
            size="lg"
            icon={on ? 'stop' : 'play'}
            onClick={async () => {
              await audio.resume();
              if (on) {
                player.current?.stop();
                player.current = null;
                setOn(false);
                setBeat(-1);
              } else {
                setOn(true);
                restart(o);
              }
            }}
          >
            {on ? 'Durdur' : 'Başlat'}
          </Btn>
          <Btn
            kind="ghost"
            icon="tap"
            onClick={() => {
              const now = performance.now();
              taps.current = taps.current.filter((t) => now - t < 2500);
              taps.current.push(now);
              const t = taps.current;
              if (t.length >= 3) {
                let sum = 0;
                for (let i = 1; i < t.length; i++) sum += (t[i] as number) - (t[i - 1] as number);
                setBpm(60000 / (sum / (t.length - 1)));
              }
            }}
          >
            Tempo vur
          </Btn>
        </div>
        <div className="metro-row">
          <div className="seg">
            {[2, 3, 4, 6].map((b) => (
              <button
                key={b}
                type="button"
                className={b === o.beats ? 'on' : undefined}
                onClick={() => update({ beats: b })}
              >
                {b}/4
              </button>
            ))}
          </div>
          <button type="button" className={`tg${o.sub ? ' on' : ''}`} onClick={() => update({ sub: !o.sub })}>
            Sekizlik
          </button>
          <button type="button" className={`tg${o.acc ? ' on' : ''}`} onClick={() => update({ acc: !o.acc })}>
            İlk vuruş vurgulu
          </button>
        </div>
        <p className="mb-note">Yeni bir akor geçişini önce 60 BPM'de temiz çal, sonra beşer beşer hızlandır.</p>
      </div>
    </ToolShell>
  );
}
